import { isNativeApp, type NativeIOSMessage } from '../helpers/isNativeApp/isNativeApp';

const LOGIN_EVENT_URL = 'lottolandtracking://EVENT_LOGIN';

type NativeGameInfo = {
  gameId: string;
  gameName: string;
  launchUrl: string;
};

/**
 * Posts a command to the iOS `LLWebHandler` message handler.
 * Returns `false` when the handler is not available, so callers can fall back to the web behaviour.
 */
function sendIOSAppEvent(command: string, value?: string): boolean {
  const iosHandler = isNativeApp()?.webkit?.messageHandlers?.LLWebHandler;
  if (typeof iosHandler?.postMessage !== 'function') return false;

  const message: NativeIOSMessage = value ? { command, value } : { command };
  iosHandler.postMessage(message);
  return true;
}

/**
 * Notifies the native app that the player logged in.
 * Mirrors `sendLoginEvent` in the legacy nativeApp bridge: the webview intercepts the custom URL.
 */
function sendLoginEvent(playerId: string) {
  if (!isNativeApp()) return;

  const rootElement = document.documentElement;
  const frame = document.createElement('iframe');
  frame.setAttribute('src', `${LOGIN_EVENT_URL}?playerid=${playerId}`);
  rootElement.appendChild(frame);
  frame.parentNode?.removeChild(frame);
}

/**
 * Notifies the native app that the player logged out.
 * Android uses `LLWebInterface`; iOS uses the `LLWebHandler` message handler.
 */
function sendLogoutEvent() {
  const nativeWindow = isNativeApp();
  if (!nativeWindow) return;

  const androidLogout = nativeWindow.LLWebInterface?.sendLogoutEvent;

  if (typeof androidLogout === 'function') {
    androidLogout();
    return;
  }

  sendIOSAppEvent('logout');
}

/**
 * Asks the iOS app to open the game in its embedded native view.
 * Mirrors `sendPlayGameEvent` in the legacy nativeApp bridge: the game info is sent as a JSON string.
 */
function sendPlayGameEvent(gameInfo: NativeGameInfo): boolean {
  return sendIOSAppEvent('playGame', JSON.stringify(gameInfo));
}

export { sendLoginEvent, sendLogoutEvent, sendPlayGameEvent, type NativeGameInfo };
